// This is tested in components/program/index.test.tsx

import SessionProgressBar from '@/components/session-progress-bar';
import { EnumButtonName } from '@calm/evexia-analytics';
import { DS_BREAKPOINT, DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import { MoreHoriz } from '@mui/icons-material';
import { Heading } from '@rhg/components/heading';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { useProgramActions } from '@rhg/hooks/use-program-actions';
import { useProvidedProgramContext } from '@rhg/hooks/use-provided-program-context';
import commonMessages from '@rhg/lang/messages/common';
import programMessages from '@rhg/lang/messages/program';
import {
    Box,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Theme,
    Typography,
    useTheme,
} from '@rhg/libs/material';
import { CheckmarkCircleOutline, InfoOutline } from '@rhg/libs/material/icons';
import { styleMaxLines } from '@rhg/libs/material/sx';
import NextImage from '@rhg/libs/next/image';
import { ProgramState } from '@rhg/utils/programs';
import { MouseEvent, useId, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ProgramNextButton } from '../next-button';
import { ProgramHeaderWrapper } from './wrapper';

interface ProgramHeaderProps {
    closeMoreMenu: () => void;
    moreAnchor: HTMLElement | null;
    openMoreMenu: (e: MouseEvent<HTMLButtonElement>) => void;
    onLeaveProgramClicked: () => void;
    onJoinProgramClicked: () => void;
    onRestartProgramClicked: () => void;
    onShowHowToGetStartedDialogClicked: () => void;
}

const styleContent = {
    maxWidth: DS_BREAKPOINT.MEDIUM,
    width: '100%',
};

const styleTitleContainer = {
    mb: 4,
};

const styleDescription = {
    ...styleMaxLines(3),
};

const styleNextButton = {
    minWidth: (theme: Theme) => theme.spacing(52),
};

const styleMoreButton = {
    borderRadius: '50%',
    color: 'action.active',
    ml: (theme: Theme) => theme.spacing(4),
};

const styleNumSessions = {
    ml: (theme: Theme) => theme.spacing(1),
};
const styleCategoryContainer = {
    display: 'flex',
    alignItems: 'center',
};
const styleNextContainer = {
    mt: DS_SPACING_UNITS.CONTENT,
};
const styleCategoryIcon = {
    display: 'flex',
    alignItems: 'center',
    pr: 2,
};

const styleProgress = {
    mt: 8,
    mb: 9,
};

export function ProgramHeader({
    closeMoreMenu,
    moreAnchor,
    openMoreMenu,
    onShowHowToGetStartedDialogClicked,
    onJoinProgramClicked,
    onLeaveProgramClicked,
    onRestartProgramClicked,
}: ProgramHeaderProps) {
    const { formatMessage } = useIntl();
    const theme = useTheme();

    const {
        program,
        programType,
        programState,
        numSessions,
        numSessionsCompleted,
        packProgram,
    } = useProvidedProgramContext();

    const moreMenuId = useId();
    const moreButtonId = useId();

    const { handleNextButton } = useProgramActions();

    const categoryIcon = useMemo(() => {
        return programType === 'clinical_program' ? (
            <NextImage
                src="/_n/images/ch-grad.png"
                alt=""
                width={24}
                height={24}
            />
        ) : undefined;
    }, [programType]);

    const prefersDarkMode = theme.palette.mode === 'dark';
    const isSmOrXs = useIsMobile();
    const description = packProgram?.description ?? program?.description;

    const moreButton = useMemo(
        () => (
            <>
                <IconButton
                    trackButton="ProgramMore"
                    sx={styleMoreButton}
                    aria-controls={moreAnchor ? moreMenuId : undefined}
                    aria-haspopup="true"
                    aria-expanded={moreAnchor ? 'true' : undefined}
                    aria-label={formatMessage(
                        programMessages['program.program_actions']
                    )}
                    onClick={openMoreMenu}
                    id={moreButtonId}
                >
                    <MoreHoriz />
                </IconButton>
                <Menu
                    id={moreMenuId}
                    open={!!moreAnchor}
                    anchorEl={moreAnchor}
                    onClose={closeMoreMenu}
                    MenuListProps={{
                        'aria-labelledby': moreButtonId,
                    }}
                >
                    {programState === ProgramState.InProgress ? (
                        <MenuItem
                            trackButton="MenuLeaveProgram"
                            onClick={onLeaveProgramClicked}
                        >
                            <ListItemIcon>
                                <CheckmarkCircleOutline />
                            </ListItemIcon>
                            <ListItemText>
                                <Typography variant="body1" color="text.body">
                                    {formatMessage(
                                        programMessages['program.leave_program']
                                    )}
                                </Typography>
                            </ListItemText>
                        </MenuItem>
                    ) : programState !== ProgramState.Completed ? (
                        <MenuItem
                            trackButton="MenuJoinProgram"
                            onClick={onJoinProgramClicked}
                        >
                            <ListItemIcon>
                                <CheckmarkCircleOutline />
                            </ListItemIcon>
                            <ListItemText>
                                <Typography variant="body1" color="text.body">
                                    {formatMessage(
                                        programMessages['program.join_program']
                                    )}
                                </Typography>
                            </ListItemText>
                        </MenuItem>
                    ) : null}
                    <MenuItem
                        trackButton="MenuHowToGetStarted"
                        onClick={onShowHowToGetStartedDialogClicked}
                    >
                        <ListItemIcon>
                            <InfoOutline />
                        </ListItemIcon>
                        <ListItemText>
                            <Typography variant="body1" color="text.body">
                                {formatMessage(
                                    programMessages[
                                        'program.how_to_get_started'
                                    ]
                                )}
                            </Typography>
                        </ListItemText>
                    </MenuItem>
                    {programState === ProgramState.Completed && (
                        <MenuItem
                            onClick={onRestartProgramClicked}
                            trackButton="MenuRestartProgram"
                        >
                            <ListItemIcon>
                                <InfoOutline />
                            </ListItemIcon>
                            <ListItemText>
                                <Typography variant="body1" color="text.body">
                                    {formatMessage(
                                        programMessages[
                                            'program.restart_program'
                                        ]
                                    )}
                                </Typography>
                            </ListItemText>
                        </MenuItem>
                    )}
                </Menu>
            </>
        ),
        [
            closeMoreMenu,
            moreAnchor,
            moreButtonId,
            moreMenuId,
            onJoinProgramClicked,
            onLeaveProgramClicked,
            onShowHowToGetStartedDialogClicked,
            onRestartProgramClicked,
            openMoreMenu,
            programState,
            formatMessage,
        ]
    );

    const commonTitleKey =
        programType === 'clinical_program'
            ? 'common.program_type.clinical_program.title'
            : 'common.program_type.undefined.title';

    return (
        <ProgramHeaderWrapper
            programImageSrc={
                packProgram?.image?.url ?? program?.backgroundImage?.url
            }
        >
            <Box sx={styleContent}>
                <Heading
                    sx={styleTitleContainer}
                    titleVariant={isSmOrXs ? 'h5' : 'h4'}
                    titleComponent="h1"
                    titleColor="text.body"
                    title={program?.title}
                    subtitle={
                        programType !== 'clinical_program'
                            ? description
                            : undefined
                    }
                    subtitleStyle={styleDescription}
                    rightButton={
                        isSmOrXs &&
                        programType === 'clinical_program' &&
                        moreButton
                    }
                />
                <Box sx={styleCategoryContainer}>
                    {categoryIcon && (
                        <Box sx={styleCategoryIcon}>{categoryIcon}</Box>
                    )}
                    <Typography
                        variant="body2"
                        component="span"
                        color={prefersDarkMode ? 'primary.main' : 'info.dark'}
                    >
                        {formatMessage(commonMessages[commonTitleKey])}
                    </Typography>
                    {!isSmOrXs && (
                        <Typography
                            variant="body2"
                            component="span"
                            sx={styleNumSessions}
                        >
                            •{' '}
                            {formatMessage(commonMessages['common.sessions'], {
                                sessions: numSessions,
                            })}
                        </Typography>
                    )}
                </Box>
                {!isSmOrXs &&
                    programState === ProgramState.InProgress &&
                    numSessions && (
                        <SessionProgressBar
                            currentStep={numSessionsCompleted}
                            totalSteps={numSessions}
                            sx={styleProgress}
                            isClincialProgram={
                                programType === 'clinical_program'
                            }
                        />
                    )}
                {!isSmOrXs && (
                    <Box sx={styleNextContainer}>
                        <ProgramNextButton
                            onClick={handleNextButton()}
                            sx={styleNextButton}
                            trackButton={EnumButtonName.ProgramHeaderNext}
                        />
                        {programType === 'clinical_program' && moreButton}
                    </Box>
                )}
            </Box>
        </ProgramHeaderWrapper>
    );
}
